import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { ComponentTable } from "@components/Tables";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import ExportSbomReportModal from "./ExportSbomReportModal";
import { useState, useMemo } from "react";
import { downloadFileDynamic } from "@utils/common";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { getSbomExportFileLabel, getSbomExportExtension, isSbomHtmlReadableExport } from "@constants/sbom";
import {
  openSbomReadableViewOrToast,
  SBOM_READABLE_VIEW_SOURCE,
} from "@utils/sbomReadableView";
import useEscapeKey from "@hooks/useEscapeKey";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { buildProductFrozenVexSnapshot } from "@constants/vex";
import dayjs from "dayjs";

const ViewMergeModal = ({
    isOpen,
    onClose,
    mergedComponents = [],
    licensePolicyData = [],
    loading = false,
    productSeq,
    productName = "",
    groupName = "",
    "data-testid": dataTestId = "view-merge-modal",
    groupProductSeq = null,
    isFromGroup = false,
    showIcon = true,
    editableFrozenVexSnapshot = false,
}) => {
    const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
    const { t } = useTranslation();
    const { colors } = useTheme();
    const clientConfig = useClientConfig();
    const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));
    const frozenVexSnapshot = useMemo(
        () =>
            buildProductFrozenVexSnapshot({
                productSeq,
                isFromGroup,
                groupProductSeq,
            }),
        [productSeq, isFromGroup, groupProductSeq],
    );
    const [showExportModal, setShowExportModal] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Handle export SBOM report
    const handleExport = async (sbomType, sbomName, includeVex = false, format = "JSON") => {
        // Format date as YYYYMMDD
        const dateString = dayjs().format("YYYYMMDD");

        // Remove spaces from productName and groupName
        const cleanProductName = productName.trim().replace(/\s+/g, "");
        const cleanGroupName = groupName.trim().replace(/\s+/g, "");
        const sbomFormat = getSbomExportFileLabel(sbomName) || String(sbomName || "").replace(/\s+/g, "");
        const extension = getSbomExportExtension(format);

        const params = {
            sbomType,
            includeVex: includeVex ? "Y" : "N",
            format,
        };

        try {
            if (isFromGroup) {
                const filename = `${cleanProductName}_${cleanGroupName}_${sbomFormat}_${dateString}.${extension}`;
                await downloadFileDynamic(filename, "groupProductReportDownloadSbom", {
                    ...params,
                    groupProductSeq: groupProductSeq || "",
                    productSeq,
                });
            } else {
                const filename = `${cleanProductName}_${sbomFormat}_${dateString}.${extension}`;
                await downloadFileDynamic(filename, "productReportDownloadSbom", {
                    ...params,
                    productSeq,
                });
            }
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportSbomReport = async (selection) => {
        const { type, format, includeVex } = selection || {};
        if (!type) return;

        setIsExporting(true);
        setShowExportModal(false);

        if (isSbomHtmlReadableExport(type)) {
            try {
                if (productSeq == null) {
                    ToastConstant(TOAST.error, t("toast.export.error.title"), t("toast.export.error.message"));
                    return;
                }
                const sourceType = isFromGroup
                    ? SBOM_READABLE_VIEW_SOURCE.GROUP_PRODUCT
                    : SBOM_READABLE_VIEW_SOURCE.PRODUCT;
                const params = {
                    productSeq,
                    includeVex: includeVex ? "Y" : "N",
                };
                if (isFromGroup) {
                    params.groupProductSeq = groupProductSeq || "";
                }
                const isDefaultGroup =
                    !groupProductSeq || groupName === "defaultgroup";
                const displayGroupName = isDefaultGroup
                    ? "Default Group"
                    : groupName;
                openSbomReadableViewOrToast({
                    sourceType,
                    params,
                    title: isFromGroup
                        ? [productName, `Group: ${displayGroupName}`]
                              .filter(Boolean)
                              .join(" / ")
                        : productName || "",
                });
            } finally {
                setIsExporting(false);
            }
            return;
        }

        if (format === "CSAF") {
            if (productSeq == null) {
                ToastConstant(TOAST.error, t("toast.export.error.title"), t("toast.export.error.message"));
                setIsExporting(false);
                return;
            }
            const dateString = dayjs().format("YYYYMMDD");
            const cleanProductName = productName.trim().replace(/\s+/g, "") || "product";
            const cleanGroupName = groupName.trim().replace(/\s+/g, "");
            const params = {
                includeVex: "Y",
                format,
                productSeq,
            };
            const filename = isFromGroup
                ? `${cleanProductName}_${cleanGroupName}_CSAF_${dateString}.json`
                : `${cleanProductName}_CSAF_${dateString}.json`;
            const endpoint = isFromGroup ? "groupProductReportDownloadSbom" : "productReportDownloadSbom";
            if (isFromGroup) {
                params.groupProductSeq = groupProductSeq || "";
            }
            downloadFileDynamic(filename, endpoint, params).finally(() => setIsExporting(false));
            return;
        }

        handleExport(type.value, type.sbomName || type.label, includeVex, format);
    };

    useEscapeKey(onClose, isOpen);

    if (!isOpen) return null;

    return (
        <>
            <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex justify-start xl:justify-center items-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
                <div className={`${colors.bgModal} max-h-[95vh] w-[95vw] min-w-[1216px] max-w-[1400px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`} data-testid={dataTestId}>
                    {/* Header */}
                    <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3 min-w-0 flex-wrap">
                                <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
                                    {t("product.merged-components") || "Merged Components"}
                                </h2>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="px-6 pb-4 flex-shrink-0">
                        <p className={`text-sm ${colors.textSecondary}`} data-testid={fieldValueTestId("description")}>
                            {t("product.merged-components-description") || "This view displays all components merged from verification(s) across multiple projects."}
                        </p>
                    </div>

                    {/* Content */}
                    <div className="flex flex-1 flex-col min-h-0 px-6 pb-4" data-testid={`${dataTestId}-content`}>
                        {loading ? (
                            <div className={`flex items-center justify-center h-64 ${colors.bgSecondary} rounded-lg`} data-testid={fieldValueTestId("loading-message")}>
                                <div className="flex items-center space-x-2">
                                    <div className={`h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                                    <p className={`text-sm ${colors.textSecondary}`}>
                                        {t("common.loading")}
                                    </p>
                                </div>
                            </div>
                        ) : mergedComponents.length === 0 ? (
                            <div className={`flex items-center justify-center h-64 ${colors.bgSecondary} rounded-lg`}>
                                <p className={`text-sm ${colors.textSecondary}`} data-testid={fieldValueTestId("no-components-message")}>
                                    {t("product.no-merged-components") || "No components found in selected verifications"}
                                </p>
                            </div>
                        ) : (
                            <ComponentTable
                                data={mergedComponents}
                                // Each component may have its own licensePolicy, licensePolicyData is used as fallback for components without one
                                licensePolicyData={licensePolicyData || []}
                                seq="mergedProductComponentSeq"
                                className="p-0"
                                scrollable={true}
                                rowHeight={40}
                                showIcon={showIcon}
                                validationMode="export"
                                showShieldFilter
                                frozenVexSnapshot={frozenVexSnapshot}
                                editableFrozenVexSnapshot={editableFrozenVexSnapshot}
                            />
                        )}
                    </div>

                    {/* Footer */}
                    <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
                        <div className="flex items-center justify-end space-x-3">
                            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
                                {t("common.cancel-button")}
                            </OutlineButton>
                            <PrimaryButton
                                data-testid={`${dataTestId}-export-button`}
                                onClick={() => setShowExportModal(true)}
                                disabled={isExporting}
                                size="md"
                            >
                                {isExporting
                                    ? t("project.verification-report.exporting")
                                    : t("project.verification-report.export-sbom-report")}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>

            <ExportSbomReportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                exporting={isExporting}
                onExport={handleExportSbomReport}
                data-testid={`${dataTestId}-export-modal`}
            />
        </>
    );
};

export default ViewMergeModal;
